import React, { useMemo } from 'react';
import { Popup } from 'semantic-ui-react';
import '../../css/FutureReadiness.css';

// ─── Event-type label map ─────────────────────────────────────────────────────

const EVENT_TYPE_LABELS = {
    pto: 'PTO',
    training: 'Training',
    sick: 'Sick',
    unavailable: 'Unavailable',
};

function toTitleCase(str) {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1).replace(/_/g, ' ');
}

function getEventTypeLabel(eventType) {
    return EVENT_TYPE_LABELS[eventType] || toTitleCase(eventType);
}

// ─── AvailabilityCell ─────────────────────────────────────────────────────────

const AvailabilityCell = ({ member }) => {
    const isAvailable = member.status === 'available';
    const label = isAvailable
        ? `${member.name}: Available`
        : `${member.name}: ${getEventTypeLabel(member.eventType) || 'Unavailable'}${member.eventTitle ? ` — ${member.eventTitle}` : ''}`;

    const indicator = (
        <td
            className={`fr-availability-cell ${isAvailable ? 'fr-availability-cell--available' : 'fr-availability-cell--unavailable'}`}
            aria-label={label}
        >
            <span
                className={`fr-availability-dot ${isAvailable ? 'fr-availability-dot--available' : 'fr-availability-dot--unavailable'}`}
                aria-hidden="true"
            >
                {isAvailable ? '✓' : '✗'}
            </span>
            {/* Visible text label for colour-blind users */}
            <div className={`fr-availability-label ${isAvailable ? 'fr-availability-label--available' : 'fr-availability-label--unavailable'}`}>
                {isAvailable ? 'Avail.' : (getEventTypeLabel(member.eventType) || 'Out')}
            </div>
        </td>
    );

    if (!isAvailable && (member.eventType || member.eventTitle)) {
        const tooltipContent = [
            getEventTypeLabel(member.eventType),
            member.eventTitle,
        ].filter(Boolean).join(' — ');

        return (
            <Popup
                content={tooltipContent}
                trigger={indicator}
                size="mini"
                inverted
                position="top center"
            />
        );
    }

    return indicator;
};

// ─── DailyReadinessGrid ───────────────────────────────────────────────────────

export const DailyReadinessGrid = ({ days }) => {
    const { members, membersByDate } = useMemo(() => {
        const seen = new Map();
        const byDate = {};
        for (const day of (days || [])) {
            for (const m of (day.memberReadiness || [])) {
                if (!seen.has(m.userId)) seen.set(m.userId, m);
            }
            byDate[day.date] = Object.fromEntries(
                (day.memberReadiness || []).map(m => [m.userId, m])
            );
        }
        return { members: Array.from(seen.values()), membersByDate: byDate };
    }, [days]);

    if (!days?.length) return null;

    return (
        <div className="fr-grid-scroll">
            <table
                className="fr-grid-table"
                aria-label="Daily member readiness"
            >
                <thead>
                    <tr>
                        <th scope="col" className="fr-grid-th-date">
                            Date
                        </th>
                        {members.map(m => (
                            <th key={m.userId} scope="col" className="fr-grid-th-member">
                                <div className="fr-grid-member-name">{m.name}</div>
                                <div className="fr-grid-member-role">{m.role}</div>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {days.map((day) => {
                        const byUserId = membersByDate[day.date] ?? {};
                        return (
                            <tr key={day.date}>
                                <th scope="row" className="fr-grid-row-date">
                                    {day.date}
                                </th>
                                {members.map(m => {
                                    const memberDay = byUserId[m.userId] ?? { ...m, status: 'available', eventType: null, eventTitle: null };
                                    return <AvailabilityCell key={m.userId} member={memberDay} />;
                                })}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};
